import {
  ProductSpecsBlock,
  ProductSpecsBlockProps,
  useCaseType,
} from '@rimactechnology/rimac-ui-lib';

import { ProductSpecsBlockFragment } from '@/api';
import getCdlPublicId from '@/hooks/useCdlPublicId';

interface EnhancedProductSpecsBlockProps {
  titleMaxWidth?: number;
  productSpecsBlock?: ProductSpecsBlockFragment | null;
  parentIndex: number;
}

export default function EnhancedProductSpecsBlock({
  titleMaxWidth,
  productSpecsBlock,
  parentIndex,
}: EnhancedProductSpecsBlockProps) {
  const productSpecsBlockProps: ProductSpecsBlockProps = {
    parentIndex: parentIndex || 1,
    titleMaxWidth,
    title: productSpecsBlock?.title || '',
    anchor: productSpecsBlock?.anchor || '',
    paragraphs: [productSpecsBlock?.subtitle || ''],
    productContents: (productSpecsBlock?.specs_options?.data || [])?.map(
      (option, index) => {
        const smallSpecTable = option?.attributes?.specTable?.data?.attributes;

        return {
          parentIndex: parentIndex || 1,
          tabItemLabel: option?.attributes?.option || '',
          tabHeading: {
            title: option?.attributes?.heading?.title || '',
            subtitle: option?.attributes?.heading?.subtitle || '',
          },
          largeSpecsTable: option.attributes?.largeSpec?.length
            ? (option.attributes?.largeSpec || []).map((spec) => ({
                label: spec?.label || '',
                value: spec?.value || '',
              }))
            : undefined,
          denseSpecTable: (option?.attributes?.denseSpecs || []).map((row) => ({
            title: row?.title || '',
            label: row?.label || '',
            value: row?.value || '',
            gapBellow: row?.gapBellow || false,
          })),
          smallSpecTable: {
            parentIndex: parentIndex || 1,
            title: smallSpecTable?.title || '',
            specs: (smallSpecTable?.specs || []).map((spec) => ({
              label: spec?.label || '',
              value: spec?.value || '',
            })),
            numOfItemsToShow: smallSpecTable?.numOfItemsToShow || undefined,
          },
          vehicleInfoContent: {
            parentIndex: index || 1,
            title: option.attributes?.VehicleInfoContent?.title || '',
            text: option.attributes?.VehicleInfoContent?.text || '',
            carItems: (
              option.attributes?.VehicleInfoContent?.carInfoItems || []
            ).map((item) => ({
              icon: {
                imgSrc: getCdlPublicId(item?.icon?.data?.attributes?.url || ''),
                alt: item?.icon?.data?.attributes?.alternativeText || '',
              },
              title: item?.title || '',
            })),
          },
          galleryImages: (option.attributes?.gallery || []).map((gallery) => ({
            imgSrc: getCdlPublicId(
              gallery?.img_desktop?.data?.attributes?.url || '',
            ),
            imgSrcMobile: getCdlPublicId(
              gallery?.img_mobile?.data?.attributes?.url || undefined,
            ),
            blurHash:
              gallery?.img_desktop.data?.attributes?.blurhash || undefined,
            alt: gallery?.alt || '',
          })),
          plots: (option.attributes?.graph || []).map((graph) => ({
            label: graph?.label || '',
            image: {
              imgSrc: getCdlPublicId(graph?.image.data?.attributes?.url || ''),
              alt: graph?.label || '',
            },
          })),
          button: option?.attributes?.button?.label
            ? {
                label: option?.attributes?.button?.label || '',
                path:
                  option?.attributes?.button?.file?.data?.attributes?.url ||
                  option?.attributes?.button?.path ||
                  '',
                useCase: 'technology' as useCaseType,
                download:
                  !!option?.attributes?.button?.file?.data?.attributes?.url,
              }
            : undefined,
        };
      },
    ),
  };

  return <ProductSpecsBlock {...productSpecsBlockProps} />;
}
